import { insightsIntl } from "../../strings/intl";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import type { ValueType } from "@wayflyer/flyui-dataviz";

import { MetricTypes } from "../../autogen/metrics";
import { formatValue } from "../../strings/type-formatting";
import type { DashboardOutput } from "../../utils/fetch-api";

import type { LtvCacChartProps } from "./components/chart";

export const getLtvCacChartData = (
  data: DashboardOutput,
  chartType: string,
  chartTypeName: string,
  periodNumBars: number,
  metricRefType: MetricTypes,
  includeFormatMethod = true,
  metricType = MetricTypes.ALLTIME_CAC,
  xAxisLabelsCol = "day"
): Optional<LtvCacChartProps, "formatValue"> => {
  const { data: chartData, formatValue } = getLtvCacMetricsChartData(
    data,
    metricType,
    metricRefType,
    xAxisLabelsCol,
    periodNumBars
  );

  return {
    data: chartData,
    currentValueName: insightsIntl.formatMessage({ id: "CAC", defaultMessage: "Customer Acquisition Cost" }),
    referenceValueName: chartTypeName,
    separateYAxis: chartType === "frequency",
    formatValue: includeFormatMethod ? formatValue : undefined,
  };
};

export const getLtvCacCustomerChartData = (
  data: DashboardOutput,
  includeFormatMethod = true,
  metricType = MetricTypes.ALLTIME_CAC,
  metricRefType = MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT,
  xAxisLabelsCol = "day"
): Optional<LtvCacChartProps, "formatValue"> => {
  const { data: chartData, formatValue } = getLtvCacMetricsChartData(data, metricType, metricRefType, xAxisLabelsCol);

  return {
    data: chartData,
    currentValueName: insightsIntl.formatMessage({ id: "CAC", defaultMessage: "Customer Acquisition Cost" }),
    referenceValueName: insightsIntl.formatMessage({ id: "Customers", defaultMessage: "Customers" }),
    separateYAxis: true,
    formatValue: includeFormatMethod ? formatValue : undefined,
  };
};

type ChartData = {
  data: LtvCacChartProps["data"];
  formatValue: (rawValue?: ValueType | null, isRef?: boolean) => string | number;
};

// convert YYYYMM to MM-YYYY
const formatCacLtvMonth = (value: string): string => {
  return moment(value, "YYYY-MM-DD").format("MM-YYYY");
};

const getLtvCacMetricsChartData = (
  data: DashboardOutput,
  metricType: MetricTypes,
  metricRefType: MetricTypes,
  xAxisLabelsCol: string,
  periodNumBars?: number
): ChartData => {
  const metric = data.metrics.find((metric) => metric.name === metricType);
  const metricRef = data.metrics.find((metric) => metric.name === metricRefType);

  const formatMetricValue = (value: ValueType, isRef = false) => {
    return formatValue({
      value: value?.toString(),
      metricType: isRef ? metricRef : metric,
      useDash: false,
      rateToFixed: 2,
    });
  };
  return {
    data: data.data?.map((row, index) => {
      const metricRefVal = +(row[metricRefType] ?? 0);
      const useStandardRefVal =
        periodNumBars === undefined || (periodNumBars && index < data.data.length - periodNumBars);
      return {
        day: formatCacLtvMonth(row[xAxisLabelsCol]),
        currentValue: +(row[metricType] ?? 0),
        referenceValue: useStandardRefVal ? metricRefVal : null,
        referenceContinuationValue: !useStandardRefVal ? metricRefVal : null,
      };
    }),
    formatValue: formatMetricValue,
  };
};
